import { FlaskConical, GitCompare, Save, Upload } from "lucide-react";

import { Button } from "./controls";
import { TonePill } from "./HealthPill";
import styles from "./integrations.module.css";
import kit from "./kit.module.css";

/**
 * Draft or live, and what can be done with it: save, try, publish, compare.
 *
 * A draft never runs. The live version keeps running while a draft is edited.
 */
export function VersionBar({
  live,
  draft,
  dirty,
  saving,
  canWrite,
  onSave,
  onTry,
  onPublish,
  onCompare,
  publishProblem,
}: {
  live: number | null;
  draft: number | null;
  dirty: boolean;
  saving: string | null;
  canWrite: boolean;
  onSave: () => void;
  onTry: () => void;
  onPublish: () => void;
  onCompare?: () => void;
  /** Why publishing is not possible yet, if it is not. */
  publishProblem?: string | null;
}) {
  return (
    <div className={kit.toolbar} role="toolbar" aria-label="Version">
      <span className={styles.inline}>
        {draft !== null ? (
          <TonePill tone="info" title="Drafts never run.">
            Draft {draft}
          </TonePill>
        ) : (
          <TonePill tone="neutral">No draft</TonePill>
        )}
        {live !== null ? (
          <TonePill tone="good" title="This version runs now.">
            Live {live}
          </TonePill>
        ) : (
          <TonePill tone="neutral">Nothing live yet</TonePill>
        )}
        {dirty && <span className={styles.warnText}>Changes not saved</span>}
      </span>
      <span className={kit.spacer} />
      {onCompare && (
        <Button tone="subtle" onClick={onCompare} disabled={draft === null || live === null}>
          <GitCompare size={15} aria-hidden />
          Compare with live
        </Button>
      )}
      <Button onClick={onSave} disabled={!canWrite || !dirty || saving !== null}>
        <Save size={15} aria-hidden />
        {saving === "save" ? "Saving…" : "Save draft"}
      </Button>
      <Button onClick={onTry} disabled={draft === null || dirty || saving !== null}>
        <FlaskConical size={15} aria-hidden />
        {saving === "try" ? "Trying…" : "Try"}
      </Button>
      <Button
        tone="primary"
        onClick={onPublish}
        disabled={!canWrite || draft === null || dirty || saving !== null || Boolean(publishProblem)}
        title={publishProblem ?? undefined}
      >
        <Upload size={15} aria-hidden />
        {saving === "publish" ? "Publishing…" : "Publish"}
      </Button>
    </div>
  );
}

/** A version compared with another, in sentences. */
export function ChangeList({ changes, empty = "No changes." }: { changes: string[]; empty?: string }) {
  if (changes.length === 0) return <p className={styles.muted}>{empty}</p>;
  return (
    <ul className={styles.lines}>
      {changes.map((line, index) => (
        <li key={`${index}-${line}`} className={styles.wrap}>
          {line}
        </li>
      ))}
    </ul>
  );
}
